import { customLog } from 'helpers/customLog';
import { useCartHydration } from 'helpers/purchaseJourney/useCartHydration';
import { useCustomerStore } from 'stores/customer.store';

import clsx from 'clsx';
import { sha256 } from 'crypto-sha';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { Route, Routes, matchPath, useLocation } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';

import { Skeleton } from '@/components/core';
import SeoHelmet from '@/components/seo';
import { LeadManager } from '@/components/ui/leads-nature/LeadManager';
import { NotificationManager } from '@/components/ui/notification/NotificationManager';
import { useAnalytics } from '@/lib/analytics';
import { PageTracker } from '@/lib/analytics/PageTracker';
import { DOMAIN, REACT_APP_ENDPOINT } from '@/lib/config';
import { isShowFooter } from '@/lib/utils';
import NotFoundPage from '@/pages/not-found';
import { sentryService } from '@/services/index';

import { useComposePageData } from './getComposePageData';
import { ContentfulLayoutSkeleton } from './layout-skeleton';
import { useNavMenuData } from './navmenuhandler';
import { useInternalMenuData } from './secondarypagehandler';
import { useHashRouter } from './useHashRouter';

const Header = React.lazy(() => import('@/components/ui/header'));
const Footer = React.lazy(() => import('@/components/ui/footer'));
const PlanViewDetails = React.lazy(() => import('@/components/ui/discovery/plan-view-details'));
const PlanQuickViewDialog = React.lazy(
  () => import('@/components/ui/variants/plan-quick-view-dialog/plan-quick-view-dialog')
);
const DialogBottom = React.lazy(() =>
  import('@/components/ui/discovery/dialog-bottom').then(module => ({
    default: module.DialogBottom,
  }))
);
const PurchaseJourneyLayout = React.lazy(() => import('../purchaseJourney-layout'));

interface ContentfulLayoutProps {
  contents: React.ReactNode[];
}

export const ComponentLoadingFallback = () => {
  return <Skeleton className="h-[100vh] w-full text-center text-white"></Skeleton>;
};

export const HeaderLoadingFallback = () => {
  return <Skeleton className="h-[145px] w-full py-10 text-center text-white"></Skeleton>;
};

export const ContentfulRoutes: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => {
  const { leftMenu, centerMenu, rightMenu, isNavMenuLoading, navMenuError } = useNavMenuData();
  const { internalMenu, isInternalMenuLoading, internalMenuError } = useInternalMenuData();
  const location = useLocation();
  const { trackSignedIn } = useAnalytics();
  const { isAuthenticated, user, isLoading } = useAuth0();
  const { pageTrackerEvent } = PageTracker();
  const [isDeferredUiReady, setIsDeferredUiReady] = useState(false);
  const [subMenu, setSubMenu] = useState<any>([]);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [notificationHeight, setNotificationHeight] = useState<number>(0);

  const {
    contentOrder,
    isContentLoading,
    seoHelmetContent,
    headerRef,
    previewLink,
    contentError,
    isPreview,
    planTabIndex,
    pathname,
    planRef,
    sectionRefs,
    contentData,
  } = useComposePageData();

  const { setIsLoggedIn, isLoggedIn } = useCustomerStore(state => state);

  useHashRouter(headerRef, location, sectionRefs, contentData);

  //to filter out the submenus for a compose page
  React.useEffect(() => {
    if (!contentData) return;

    const firstPage = contentData.NBJComposePages?.[0]; // safely get first page
    const subMenuFields = firstPage?.subMenu?.fields; // safely get fields

    if (subMenuFields) {
      setSubMenu(subMenuFields);
    } else {
      setSubMenu([]); // fallback if no subMenu or empty array
    }
  }, [contentData]);

  useCartHydration();

  React.useEffect(() => {
    if (sentryService.isSentryServiceEnabled()) {
      sentryService.traceGlobalError();
      sentryService.traceFetchNetworkStatusFailed();
      sentryService.traceXHRNetworkStatusFailed();
    }
  }, []);

  React.useEffect(() => {
    if (globalThis.window === undefined) return;
    let timeoutId: number | undefined;

    const schedule = () => {
      timeoutId = globalThis.window.setTimeout(() => setIsDeferredUiReady(true), 2000);
    };

    if (document.readyState === 'complete') {
      schedule();
      return () => {
        if (timeoutId) globalThis.window.clearTimeout(timeoutId);
      };
    }

    const onLoad = () => {
      schedule();
    };

    globalThis.window.addEventListener('load', onLoad, { once: true });

    return () => {
      globalThis.window.removeEventListener('load', onLoad);
      if (timeoutId) globalThis.window.clearTimeout(timeoutId);
    };
  }, []);

  const getEmailHash = async (email: string) => {
    if (email) {
      try {
        return await sha256(email);
      } catch (error) {
        sentryService.captureException(error);
        return undefined;
      }
    } else {
      // console.log('No email available to hash.');
      return undefined;
    }
  };

  useEffect(() => {
    pageTrackerEvent(seoHelmetContent && seoHelmetContent.title ? seoHelmetContent.title : null);
  }, [seoHelmetContent, isLoading, pageTrackerEvent]);

  React.useEffect(() => {
    if (!isLoggedIn) {
      const triggerEvent = async () => {
        if (isAuthenticated && user?.email) {
          setIsLoggedIn(true);
          const emailId = await getEmailHash(user?.email);
          customLog('user emailId tracking', emailId);
          trackSignedIn({
            userEmailHashed: emailId,
            userId: user?.sub,
          });
        }
      };
      setTimeout(() => {
        triggerEvent();
      }, 1000); // 1 second delay for safety
    }
  }, [isAuthenticated, isLoggedIn]);

  if (navMenuError || contentError || internalMenuError) {
    let message = '';
    if (navMenuError) {
      message += `${navMenuError.message}`;
    }
    if (internalMenuError) {
      message += `${internalMenuError.message}`;
    }
    if (contentError) {
      message += `${contentError.message} - ${contentError.sysId}`;
    }

    return (
      <>
        {!isPreview && (
          <div className="flex h-[100vh] content-center items-center justify-center font-font-family-primary text-xl font-bold leading-6 text-color-red-red">
            Error : {message}
          </div>
        )}
      </>
    );
  }

  if (isNavMenuLoading || isContentLoading || isInternalMenuLoading) {
    return <ContentfulLayoutSkeleton />;
  }

  let nestedMenu: any = [];
  if (centerMenu) {
    centerMenu.forEach((item: any) => {
      if (item.sectionLinks) {
        nestedMenu = [...nestedMenu, ...item.sectionLinks];
      }
    });
  }

  const routePaths = [...previewLink, ...centerMenu, ...internalMenu, ...nestedMenu]
    ?.map(({ link, itemUrl }) => link || itemUrl)
    .filter(Boolean);
  const isNotFoundRoute =
    !isNavMenuLoading &&
    !isContentLoading &&
    !isInternalMenuLoading &&
    !routePaths.some(path => matchPath({ path, end: true }, location.pathname)) &&
    !matchPath({ path: '/plans/:id', end: true }, location.pathname) &&
    !matchPath({ path: '/build-my-plan/checkout', end: true }, location.pathname) &&
    !matchPath({ path: '/build-my-plan/checkout/saved-cart', end: true }, location.pathname);

  return (
    <div className={className}>
      {seoHelmetContent ? <SeoHelmet data={seoHelmetContent} /> : null}
      <CookiesProvider>
        <NotificationManager
          notifAgent={REACT_APP_ENDPOINT}
          domain={DOMAIN}
          notificationRef={notificationRef}
          setNotificationHeight={setNotificationHeight}
        />
      </CookiesProvider>
      <Suspense fallback={<HeaderLoadingFallback />}>
        <Header
          centerMenu={centerMenu}
          rightMenu={rightMenu}
          leftMenu={leftMenu}
          headerRef={headerRef}
          planRef={planRef}
          subMenu={subMenu}
          notificationHeight={notificationHeight}
          cartURLForViewBundleRef={contentData?.NBJComposePages?.[0]?.cartUrl ?? null}
        />
      </Suspense>
      <main
        className={clsx('flex min-h-[57vh] w-full flex-col items-center justify-start')}
        {...props}
      >
        <Routes>
          {[...previewLink, ...centerMenu, ...internalMenu, ...nestedMenu]?.map(
            ({ link, text, itemUrl, name }, index: number) => {
              return (
                <Route
                  path={link || itemUrl}
                  key={`route-${text || name}-${index}`}
                  element={<ContentfulLayout contents={contentOrder} />}
                />
              );
            }
          )}
          <Route
            path={'/plans/:id'}
            key={`route-${`plan-view-details`}`}
            element={
              <Suspense fallback={<ComponentLoadingFallback />}>
                <PlanViewDetails />
              </Suspense>
            }
          />
          <Route
            path="/build-my-plan/checkout/saved-cart"
            element={
              <Suspense fallback={<ComponentLoadingFallback />}>
                <PurchaseJourneyLayout notificationHeight={notificationHeight} />
              </Suspense>
            }
          />

          <Route
            path={'/build-my-plan/checkout'}
            key={`route-build-my-plan`}
            element={
              <Suspense fallback={<ComponentLoadingFallback />}>
                <PurchaseJourneyLayout notificationHeight={notificationHeight} />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <LeadManager apiEndpoint={REACT_APP_ENDPOINT} isSignedIn={isAuthenticated} />

      {isShowFooter(planTabIndex, pathname) && !isNotFoundRoute && (
        <Suspense fallback={<HeaderLoadingFallback />}>
          <Footer />
        </Suspense>
      )}
      {isDeferredUiReady ? (
        <Suspense fallback={null}>
          <div>
            <PlanQuickViewDialog />
            <DialogBottom />
          </div>
        </Suspense>
      ) : null}
    </div>
  );
};

export const ContentfulLayout: React.FC<ContentfulLayoutProps> = ({ contents }) => {
  return (
    <>
      {contents?.map((item: any, index: number) => (
        <Suspense fallback={<ComponentLoadingFallback />} key={`contentful-section-${index}`}>
          <div key={`contentful-content-${index}`}>
            <React.Fragment>{item}</React.Fragment>
          </div>
        </Suspense>
      ))}
    </>
  );
};
